<script lang="ts">
	/**
	 * `/Account/Subscription/:uuid` — the one screen in the account area with real rules
	 * (Design §9.3, §9.4, §9.5).
	 *
	 * Three of them are load-bearing:
	 *
	 * - **The two undo affordances are labelled distinctly and only one is ever offered.** *Keep my
	 *   subscription* clears a pending cancellation; *Cancel scheduled downgrade to Pro* releases a
	 *   scheduled plan change. Both otherwise read as "undo", and a customer who clicks the wrong
	 *   one either loses access they meant to keep or keeps paying for a tier they meant to leave.
	 *   The `{:else if}` below is what makes "only one at a time" structural rather than a promise.
	 * - **A change is previewed before it is made.** Showing a prorated charge only after taking it
	 *   is a reliable way to generate disputes, so the confirm button does not exist until the
	 *   server has said what the change costs.
	 * - **`requires_action` is navigated to.** An SCA challenge is routine in the EU and this
	 *   catalog prices in EUR. Swallowing it means the customer is charged nothing, granted
	 *   nothing, and told it worked.
	 */

	import { onMount } from 'svelte';
	import {
		Breadcrumb,
		Button,
		Checkbox,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		AcceptedTerms,
		SelectedOptions,
		ConfirmButton,
		EmptyState,
		PageHeader,
		Panel,
		InvoiceDocumentLinks,
		PriceDisplay,
		RoutedBreadcrumbItem,
		RoutedLink,
		RoutedButton,
		SkeletonWrapper,
		SubscriptionStatusBadge,
		SubscriptionSkeleton,
		Bone
	} from '../../components';
	import {
		cancelPendingChange,
		cancelSubscriptionAtPeriodEnd,
		getAccountSubscription,
		getCatalogProduct,
		listAccountInvoices,
		listCatalogProducts,
		resumeSubscription
	} from '../../AppNetwork';
	import { availableCurrencies, currencyRow, formatAmount, subscriptionPriceLine } from '../../stores/storefront';
	import {
		billingPeriodName,
		accessEndsAt,
		featureSectionsOf,
		formatCalendarDate,
		formatInterval,
		invoicePeriodLabel,
		invoiceReference,
		invoiceStatusLabel
	} from '../../Utilities';
	import { toastError, toastSuccess } from '../../stores/toast';
	import type { AccountInvoice, AccountSubscription, Price, Product } from '../../AppTypes';

	export let params: { uuid?: string } = {};

	let subscription: AccountSubscription | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	/** Names the action in flight, so only the button that started it shows as busy. */
	let busy: string = '';

	/** Sibling tiers in the same family. Absent is a rendering state, never an error. */
	let alternatives: Product[] = [];
	/**
	 * The product held, as the catalogue describes it — read for the alternatives anyway, and kept so
	 * the cancel dialog can list what is about to be lost. Null when the read failed or the product
	 * has been archived; the dialog then says it in words alone.
	 */
	let heldProduct: Product | null = null;
	$: lostFeatures = featureSectionsOf(heldProduct?.comparisonFeatures);
	/** When access actually ends if they cancel now: the end of the period already paid for. */
	$: cancelEffectiveAt = subscription === null ? null : (subscription.endsAt ?? subscription.currentPeriodEnd);

	/**
	 * What this subscription has actually been billed (§4.6).
	 *
	 * The page said what the *next* charge would be and nothing about the ones already taken —
	 * which is the half a customer is looking for when they arrive from a bank statement they do
	 * not recognise. The archive at `/Account/Invoices` has them, mixed in with every other
	 * purchase; here they are the ones for this subscription and no others.
	 *
	 * Narrowed **server-side**: filtering a fetched page here would silently drop everything past
	 * the first page and leave the count reporting rows that are not on the screen.
	 */
	let invoices: AccountInvoice[] = [];
	let invoiceCount: number = 0;
	let invoicesLoading: boolean = false;
	/** Swallowed like the alternatives: a billing history that will not load must not take the
	 *  page down with it — cancel, resume and the pending-change undo all still work. */
	let invoicesFailed: boolean = false;

	/** The most recent few. The archive is where "all of them" lives, and it is linked to. */
	const INVOICE_PREVIEW = 5;

	/**
	 * The same words the archive uses. `open` is the state a customer has to act on, and calling
	 * it "open" tells them nothing — two screens describing one status differently is two screens
	 * a customer has to reconcile.
	 */

	async function fnLoadInvoices(subscriptionUUID: string): Promise<void> {
		invoicesLoading = true;
		invoicesFailed = false;
		try {
			const result = await listAccountInvoices({ subscriptionUUID, limit: INVOICE_PREVIEW });
			invoices = result.items;
			invoiceCount = result.total;
		} catch {
			invoices = [];
			invoiceCount = 0;
			invoicesFailed = true;
		} finally {
			invoicesLoading = false;
		}
	}


	$: canChangeSeats =
		subscription !== null &&
		subscription.isSeatBased &&
		pendingChange === null &&
		!subscription.cancelAtPeriodEnd &&
		['active', 'trialing', 'past_due'].includes(subscription.status);


	$: uuid = params.uuid ?? '';
	/** This subscription's invoices — the whole list arrives filtered rather than store-wide. */
	$: invoicesHref = `/Account/Invoices?subscription=${encodeURIComponent(uuid)}`;

	async function fnLoad(subscriptionUUID: string): Promise<void> {
		if (subscriptionUUID.length === 0) return;

		loading = true;
		loadError = '';
		try {
			subscription = await getAccountSubscription(subscriptionUUID);
			void fnLoadInvoices(subscriptionUUID);
			await fnLoadAlternatives(subscription);
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/**
	 * The tiers this subscription could move to.
	 *
	 * Each price arrives carrying the server's own `action` discriminator — `upgrade`,
	 * `downgrade`, `current`, `unavailable` — computed from `Product.rank` and this customer's
	 * subscription. It is never re-derived here: §9.3's rank rule lives in one place, and a client
	 * with its own opinion eventually offers an upgrade the API refuses.
	 *
	 * A failure is swallowed on purpose. Not being able to list alternatives must not take the
	 * detail page down with it — cancel, resume and the pending-change undo all still work.
	 */
	async function fnLoadAlternatives(current: AccountSubscription): Promise<void> {
		alternatives = [];
		heldProduct = null;
		try {
			const product = await getCatalogProduct(current.productSlug, { currency: current.currency });
			heldProduct = product;
			if (product.familySlug === null) return;

			const family = await listCatalogProducts({
				familySlug: product.familySlug,
				currency: current.currency
			});
			alternatives = family.items;
		} catch {
			alternatives = [];
		}
	}


	onMount(() => {
		void fnLoad(uuid);
	});

	/** Every price in the family that is not the one already held and can actually be moved to. */
	$: options = alternatives
		.flatMap(product => product.prices.map(price => ({ product, price })))
		.filter(
			({ price }) =>
				price.uuid !== subscription?.priceUUID &&
				price.isActive &&
				!price.unavailable &&
				price.action !== 'current'
		)
		.sort((left, right) => left.product.rank - right.product.rank);

	/**
	 * The same options, grouped by **tier**.
	 *
	 * A product with a monthly and a yearly price used to appear as two entries with the same
	 * name, distinguished only by a price line — which reads as two different plans, and reads
	 * *especially* badly when one is an upgrade and the other a downgrade. The tier is named once
	 * and its billing periods sit under it as the choices they are.
	 */
	$: optionGroups = options.reduce<{ product: Product; prices: Price[] }[]>((groups, entry) => {
		const existing = groups.find(group => group.product.uuid === entry.product.uuid);
		if (existing === undefined) {
			groups.push({ product: entry.product, prices: [entry.price] });
		} else {
			existing.prices.push(entry.price);
		}
		return groups;
	}, []);

	/** "Daily" / "Monthly" / "Yearly" / "Every 3 months" — the choice within a tier, as a period. */
	function billingPeriodLabel(price: Price): string {
		if (price.interval === null) return 'One-off';
		if (price.intervalCount === 1) {
			// Every interval named, not a two-way branch: `interval === 'month' ? … : 'Yearly'`
			// labelled a daily plan "Yearly" the moment a third interval existed, and would do it
			// again for the next one. Capitalised from the shared adverb so this and the storefront
			// cannot drift into two names for one period.
			const adverb = billingPeriodName(price).replace(' billing', '');
			return adverb.charAt(0).toUpperCase() + adverb.slice(1);
		}
		return `Every ${price.intervalCount} ${price.interval}s`;
	}

	function actionLabel(price: Price): string {
		switch (price.action) {
			case 'upgrade':
				return 'Upgrade (charged now)';
			case 'downgrade':
				return 'Downgrade (starts at period end)';
			case 'unavailable':
				return 'Not available';
			default:
				return 'Change to this plan';
		}
	}

	/** Applies whatever a mutation answered with, re-reading when it answered with nothing. */
	async function fnApply(
		action: string,
		mutate: (subscriptionUUID: string) => Promise<AccountSubscription | null>,
		success: string,
		failureHeadline: string
	): Promise<void> {
		if (subscription === null) return;

		busy = action;
		const subscriptionUUID = subscription.uuid;
		try {
			const updated = await mutate(subscriptionUUID);
			subscription = updated ?? (await getAccountSubscription(subscriptionUUID));
			toastSuccess(success);
		} catch (error) {
			toastError(failureHeadline, (error as Error).message);
		} finally {
			busy = '';
		}
	}

	$: coupon = subscription?.coupon ?? null;
	$: pendingChange = subscription?.pendingChange ?? null;
	/**
	 * Through the shared helper, so this page, the subscriptions list and the account summary
	 * cannot drift about how a per-seat plan is described (§9.7). On a per-seat plan the unit
	 * amount alone is actively misleading: "$20/month" beside a ten-user team states a figure
	 * nowhere near what they pay, and it is wrong in the direction that looks cheap.
	 */
	$: priceLine = subscription === null ? '' : $subscriptionPriceLine(subscription);

	// Only a live subscription can be cancelled, and one already cancelling is offered the undo
	// below instead — never both a cancel and a keep on the same screen.
	$: canCancel =
		subscription !== null &&
		!subscription.cancelAtPeriodEnd &&
		['active', 'trialing', 'past_due', 'unpaid'].includes(subscription.status);
</script>

<Breadcrumb class="mb-4">
	<!--
		Two crumbs, not three. The account landing page and the purchases list are one page now, so
		a separate "Account" crumb above "Purchases" would be the same destination twice — which
		reads as a level of hierarchy that is not there.
	-->
	<RoutedBreadcrumbItem href="/Account" home>Purchases</RoutedBreadcrumbItem>
	<RoutedBreadcrumbItem>{subscription?.productName ?? 'Subscription'}</RoutedBreadcrumbItem>
</Breadcrumb>

{#if loading}
	<SkeletonWrapper>
		<SubscriptionSkeleton />
	</SkeletonWrapper>
{:else if loadError.length > 0}
	<EmptyState variant="error" title="This subscription could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={() => fnLoad(uuid)}>Try again</Button>
		<RoutedButton href="/Account/Subscriptions" color="alternative" size="sm">All subscriptions</RoutedButton>
	</EmptyState>
{:else if subscription !== null}
	<PageHeader title={subscription.productName}>
		<svelte:fragment slot="description">{priceLine}</svelte:fragment>
	</PageHeader>

	<div class="grid gap-6 lg:grid-cols-3 items-start">
		<div class="lg:col-span-2 space-y-6">
			<Panel title="Status" faIcon="circle-info">
				<svelte:fragment slot="titleRight">
					<SubscriptionStatusBadge
						forCustomer
						status={subscription.status}
						cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
					/>
				</svelte:fragment>

				<dl class="grid gap-3 sm:grid-cols-2 text-sm">
					{#if subscription.currentPeriodEnd !== null}
						<div>
							<dt class="card-label mb-1">
								{subscription.cancelAtPeriodEnd ? 'Access ends' : 'Next charge'}
							</dt>
							<dd>{formatCalendarDate(accessEndsAt(subscription) ?? subscription.currentPeriodEnd)}</dd>
						</div>
					{/if}
					{#if subscription.trialEnd !== null}
						<div>
							<dt class="card-label mb-1">Trial ends</dt>
							<dd>{formatCalendarDate(subscription.trialEnd)}</dd>
						</div>
					{/if}
					<div>
						<dt class="card-label mb-1">Started</dt>
						<dd>{formatCalendarDate(subscription.createdAt)}</dd>
					</div>
					{#if coupon !== null}
						<div>
							<dt class="card-label mb-1">Discount</dt>
							<dd>
								{coupon.discountType === 'percent' && coupon.percentOff !== null
									? `${coupon.percentOff}% off`
									: coupon.code}
								<span class="text-default">
									{coupon.duration === 'forever'
										? '· for as long as you subscribe'
										: coupon.duration === 'once'
											? '· one billing period'
											: `· ${coupon.durationInMonths ?? 0} months`}
								</span>
							</dd>
						</div>
					{/if}
				</dl>

				{#if subscription.graceUntil !== null}
					<!--
						The grace sentence (§9.5). This is why `graceUntil` is in the payload at all: a
						customer whose card failed still has access, and the only thing they need from
						this page is the date by which to fix it.
					-->
					<div class="widget bg-depth border-widget mt-4 flex flex-wrap items-center gap-3">
						<i class="fa-solid fa-triangle-exclamation text-yellow-500" />
						<div class="grow min-w-0 text-sm">
							Update your payment method by {formatCalendarDate(subscription.graceUntil)} to keep access.
						</div>
						<RoutedButton href="/Account/Billing" color="primary" size="sm">
							Update payment method
						</RoutedButton>
					</div>
				{/if}

				{#if pendingChange !== null}
					<div class="widget bg-depth border-widget mt-4 text-sm">
						<div class="font-semibold">
							Scheduled change to {pendingChange.productName}
						</div>
						<p class="text-default">
							It takes effect on {formatCalendarDate(pendingChange.effectiveAt)}
							{#if $formatAmount(pendingChange.amount, pendingChange.currency) !== null}
								, at {$formatAmount(pendingChange.amount, pendingChange.currency)}{formatInterval(
									pendingChange.interval,
									pendingChange.intervalCount
								)}
							{/if}. You keep {subscription.productName} until then.
						</p>
					</div>
				{/if}
			</Panel>

			<!--
				Seats (§9.7). Above the tier change because it is the far more common operation on a
				team plan, and because only one change of either kind can be in flight at a time.
			-->
			{#if subscription.isSeatBased}
				<Panel title="Users" faIcon="users">
					<svelte:fragment slot="titleRight">
						<span class="text-sm text-default">{subscription.quantity} in use</span>
					</svelte:fragment>

					{#if pendingChange !== null}
						<EmptyState
							title="A change is already scheduled."
							description="Cancel it first if you want to change users instead."
						/>
					{:else if !canChangeSeats}
						<EmptyState
							title="Users cannot be changed right now."
							description="This subscription is ending or is not currently active."
						/>
					{:else}
						<!--
							The control lives on the product page now, with the quote and the confirm
							step. It used to be here, which meant the store asked "are you sure" about
							money in two places, in two layouts, with two sets of words — and the seat
							one could not show what the tier it was changing actually included.
						-->
						<p class="text-sm text-default">
							You have {subscription.quantity}
							{subscription.quantity === 1 ? 'user' : 'users'} on {subscription.productName}. Adding
							users is charged straight away; removing them takes effect at the end of the period
							you have already paid for.
						</p>
						<RoutedButton
							href={`/Product/${subscription.productSlug}`}
							color="primary"
							size="sm"
							class="mt-3"
						>
							<i class="fa-solid fa-users mr-2" />Change users
						</RoutedButton>
					{/if}
				</Panel>
			{/if}

			<!--
				What this subscription has actually been billed. The page said what the *next* charge
				would be and nothing about the ones already taken — which is the half a customer is
				looking for when they arrive from a bank statement they do not recognise.
			-->
			<Panel title="Invoices" faIcon="file-invoice">
				<svelte:fragment slot="titleRight">
					{#if invoiceCount > INVOICE_PREVIEW}
						<RoutedLink to={invoicesHref} class="text-sm text-primary-500 hover:underline">
							All {invoiceCount} invoices
						</RoutedLink>
					{/if}
				</svelte:fragment>

				{#if invoicesLoading}
					<!-- Rows the shape of the table below, so the panel does not change height when it lands. -->
					<div role="status" aria-label="Loading your invoices" class="space-y-3 animate-pulse">
						{#each Array(3) as _row, index (index)}
							<div class="grid grid-cols-4 gap-4">
								<Bone class="h-3 w-3/4" />
								<Bone class="h-3 w-1/2" />
								<Bone class="h-3 w-1/2" />
								<Bone class="h-3 w-2/3" />
							</div>
						{/each}
					</div>
				{:else if invoicesFailed}
					<EmptyState
						variant="error"
						compact
						title="Your invoices could not be loaded."
						description="Everything else on this page still works. The full history is in your account."
					/>
				{:else if invoices.length === 0}
					<!-- Not an error: a subscription in its trial has been billed nothing yet. -->
					<EmptyState
						compact
						title="Nothing has been billed yet."
						description="Invoices appear here as each period is charged."
					/>
				{:else}
					<!--
						Four columns, the date under the reference: this table sits in two thirds of
						the page, and at five the Document column — the one a customer comes here
						for — was scrolled out of view at ordinary widths.
					-->
					<Table divClass="relative overflow-x-auto">
						<TableHead>
							<TableHeadCell>Invoice</TableHeadCell>
							<TableHeadCell>Amount</TableHeadCell>
							<TableHeadCell>Status</TableHeadCell>
							<TableHeadCell>Document</TableHeadCell>
						</TableHead>
						<TableBody>
							{#each invoices as invoice (invoice.uuid)}
								<TableBodyRow>
									<TableBodyCell class="dataTable">
										<div>{invoiceReference(invoice)}</div>
										{#if invoicePeriodLabel(invoice).length > 0}
											<!-- Through the shared helper, which also withholds a
											     range whose ends render as one date — see
											     `invoicePeriodLabel`. -->
											<div class="text-xs text-default">{invoicePeriodLabel(invoice)}</div>
										{/if}
										<div class="text-xs text-default">
											{invoice.issuedAt !== null
												? `Issued ${formatCalendarDate(invoice.issuedAt)}`
												: 'Not issued yet'}
										</div>
									</TableBodyCell>
									<TableBodyCell class="dataTable">
										<!-- Formatted from the whole currency row, never ÷100 (§10). -->
										{$formatAmount(invoice.total, invoice.currency) ?? 'Amount unavailable'}
										{#if invoice.status === 'open' && invoice.amountRemaining !== null && invoice.amountRemaining > 0}
											<div class="text-xs text-yellow-600 dark:text-yellow-400">
												{$formatAmount(invoice.amountRemaining, invoice.currency) ?? ''} still owing
											</div>
										{/if}
									</TableBodyCell>
									<TableBodyCell class="dataTable">{invoiceStatusLabel(invoice)}</TableBodyCell>
									<TableBodyCell class="dataTable">
										<!-- The shared control: the ours-or-theirs rule and the `documentNumber`
										     test live in one place, not once per screen that lists invoices. -->
										<InvoiceDocumentLinks {invoice} />
									</TableBodyCell>
								</TableBodyRow>
							{/each}
						</TableBody>
					</Table>
				{/if}
			</Panel>

			<Panel title="Change plan" faIcon="arrow-right-arrow-left">
				{#if options.length === 0}
					<EmptyState
						title="There is nothing else to move to."
						description="This product has no other tier available on your currency."
					/>
				{:else if pendingChange !== null}
					<!-- A second change on top of a scheduled one has no coherent meaning at the
					     provider: the schedule already owns the next period. -->
					<EmptyState
						title="A change is already scheduled."
						description={`Cancel the scheduled change to ${pendingChange.productName} first if you want to do something else.`}
					/>
				{:else}
					<!--
						One card per tier, each linking to its own page.
						
						The quote and the confirm step live there now. They used to live here, which
						put the money-moving path on a screen that could describe the tier being moved
						to only by its name and its price — the page that actually describes it was
						one click away, and the customer had just come from it. Sending them there
						instead means one screen sells a tier, whichever direction the move goes in.
					-->
					<ul class="space-y-3">
						{#each optionGroups as group (group.product.uuid)}
							<li class="widget bg-depth">
								<div class="flex flex-wrap items-start gap-3">
									<div class="grow min-w-0">
										<div class="font-semibold">{group.product.name}</div>
										{#if group.product.tagline}
											<p class="text-sm text-default mt-0.5">{group.product.tagline}</p>
										{/if}
									</div>
									<RoutedButton
										href={`/Product/${group.product.slug}`}
										color="alternative"
										size="sm"
									>
										View and change
									</RoutedButton>
								</div>

								<!--
									The billing periods, with the direction of each move. The direction is
									a property of the *price*, not the tier — monthly Pro can be an
									upgrade while yearly Pro is a downgrade — so the label stays on the
									row rather than moving up to the heading.
								-->
								<ul class="mt-3 space-y-2">
									{#each group.prices as price (price.uuid)}
										<li class="flex flex-wrap items-center gap-3 rounded-lg px-3 py-2 bg-depth">
											<div class="grow min-w-0">
												<div class="text-sm font-medium">{billingPeriodLabel(price)}</div>
												<PriceDisplay
													{price}
													currency={$currencyRow(price.currency)}
													currencyCode={price.currency}
													showTaxNote={false}
													showTrial={false}
													size="sm"
												/>
											</div>
											<span class="text-sm text-default">{actionLabel(price)}</span>
										</li>
									{/each}
								</ul>
							</li>
						{/each}
					</ul>
				{/if}
			</Panel>

			<!--
				What this purchase was agreed to, and what the customer typed into it (§4.7).

				The same record support reads on the admin sale screen, through the same component,
				because two renderings of one piece of evidence is a support conversation with
				nothing left to resolve it. Rendered only when there is something in it: a customer
				whose plan required no terms would otherwise be shown a panel explaining an absence
				they never wondered about, in words written for whoever authors the catalogue.

				Last in the wide column, because it is reference material — read when a question
				comes up, not on the way past.
			-->
			{#if subscription.acceptedTerms !== undefined && subscription.acceptedTerms.length > 0}
				<AcceptedTerms acceptances={subscription.acceptedTerms} />
			{/if}

			<!--
				§4.8 — the options this was bought with, and what each took off. Rendered only when
				there is something in it, for the reason the terms panel is: the empty state is
				written for whoever authors the catalogue, and a plan bought with no options is not
				an absence a customer wondered about.
			-->
			{#if subscription.selectedOptions !== undefined && subscription.selectedOptions.length > 0}
				<!--
					No `showKey` here, and deliberately: the application key is a wire value for
					whoever wrote the integration, not for the person who bought the plan. The two
					admin sale screens pass it; this page shows the wording and the money.
				-->
				<SelectedOptions selections={subscription.selectedOptions} currencies={$availableCurrencies} />
			{/if}
		</div>

		<div class="space-y-6">
			<Panel title="Manage" faIcon="sliders">
				<div class="flex flex-col gap-3">
					{#if subscription.cancelAtPeriodEnd}
						<!--
							Undo #1. Distinct wording from #2 below, and structurally exclusive with it:
							"Keep my subscription" restores a subscription that is on its way out, while
							"Cancel scheduled downgrade" is about a tier change that has not happened yet.
							One label for both would be an undo button whose effect the customer cannot
							predict.
						-->
						<div class="text-sm text-default">
							This subscription is set to end
							{#if accessEndsAt(subscription) !== null}
								on {formatCalendarDate(accessEndsAt(subscription) ?? '')}
							{/if}.
						</div>
						<Button
							color="primary"
							size="sm"
							disabled={busy === 'resume'}
							on:click={() =>
								void fnApply(
									'resume',
									resumeSubscription,
									'Your subscription will carry on as normal.',
									'The subscription was not resumed.'
								)}
						>
							Keep my subscription
						</Button>
					{:else if pendingChange !== null}
						<!-- Undo #2. -->
						<Button
							color="alternative"
							size="sm"
							disabled={busy === 'pendingChange'}
							on:click={() =>
								void fnApply(
									'pendingChange',
									cancelPendingChange,
									'The scheduled change has been called off.',
									'The scheduled change was not cancelled.'
								)}
						>
							Cancel scheduled downgrade to {pendingChange.productName}
						</Button>
					{/if}

					{#if canCancel}
						<ConfirmButton
							color="red"
							size="sm"
							disabled={busy === 'cancel'}
							action="cancel this subscription"
							confirmText={`Are you sure you want to cancel ${subscription.productName}?`}
							declineLabel="Don't cancel"
							acceptLabel="Cancel subscription"
							on:confirm={() =>
								void fnApply(
									'cancel',
									cancelSubscriptionAtPeriodEnd,
									'Cancelled. You keep access until the end of the period you have paid for.',
									'The subscription was not cancelled.'
								)}
						>
							<!--
								What the decision costs, stated before it is made: the date access stops,
								that what is kept in the product may go with it, and — where the catalogue
								still describes the tier — the list of what stops working.
							-->
							<svelte:fragment slot="detail">
								<p class="text-sm">
									You keep access until
									<strong>{cancelEffectiveAt !== null ? formatCalendarDate(cancelEffectiveAt) : 'the end of the period you have paid for'}</strong>.
									After that you <strong>lose access to everything below</strong>, and anything you keep in
									{subscription.productName} may be deleted.
								</p>
								{#if lostFeatures.length > 0}
									<div class="mt-3 max-h-64 overflow-y-auto rounded-2xl bg-depth p-3 text-sm space-y-3" data-testid="cancel-lost-features">
										{#each lostFeatures as section, index (index)}
											<div>
												{#if section.title !== null}
													<p class="card-label mb-1">{section.title}</p>
												{/if}
												<ul class="space-y-1">
													{#each section.rows as row, rowIndex (rowIndex)}
														<li class="flex gap-2">
															<i class="fa-solid fa-xmark text-red-500 mt-1 text-xs" aria-hidden="true" />
															<span>{row.label}{row.value !== null ? `: ${row.value}` : ''}</span>
														</li>
													{/each}
												</ul>
											</div>
										{/each}
									</div>
								{/if}
							</svelte:fragment>
							Cancel subscription
						</ConfirmButton>
					{/if}

					<RoutedButton
						href={invoicesHref}
						color="alternative"
						size="sm"
					>
						Invoices for this subscription
					</RoutedButton>
				</div>
			</Panel>
		</div>
	</div>
{/if}
